/**
 * The canonical session snapshot the API offers when its charger-UX mode is
 * on: `session.canonical` on session reads and `session.snapshot` events on
 * the session stream. When the API does not send one, nothing here applies
 * and every screen behaves as it did before.
 *
 * This module only decides which snapshot to keep. It never works out a
 * session state by itself — the state, the reason and the wording come from
 * the API (the backend owns the transition rules).
 */

export const SNAPSHOT_SCHEMA_VERSION = 1;

export const SESSION_STATE = Object.freeze({
  PREPARING: 'PREPARING',
  CHARGING: 'CHARGING',
  PAUSED: 'PAUSED',
  RESUMING: 'RESUMING',
  COMPLETED: 'COMPLETED',
  ENDED_EARLY: 'ENDED_EARLY',
  FAILED_TO_START: 'FAILED_TO_START',
  STATUS_UNKNOWN: 'STATUS_UNKNOWN',
  CHARGER_UNAVAILABLE: 'CHARGER_UNAVAILABLE',
});
export const SESSION_STATES = Object.freeze(Object.values(SESSION_STATE));

const TERMINAL = new Set([SESSION_STATE.COMPLETED, SESSION_STATE.ENDED_EARLY, SESSION_STATE.FAILED_TO_START]);
export const isTerminalState = (state) => TERMINAL.has(state);

/** A snapshot this build can render: right shape, a schema it understands. */
export function isCanonicalSnapshot(value) {
  return !!value && typeof value === 'object'
    && typeof value.sessionUid === 'string'
    && Number.isInteger(value.version)
    && typeof value.sessionState === 'string'
    && Number.isInteger(value.schemaVersion) && value.schemaVersion <= SNAPSHOT_SCHEMA_VERSION;
}

export const DECISION = Object.freeze({
  APPLY: 'apply',
  DUPLICATE: 'duplicate', // same version: nothing changes, no UI effect is replayed
  STALE: 'stale',         // older than what is shown: ignored
  REGRESSION: 'regression', // would move a finished session back: ignored, refetch
  INVALID: 'invalid',
});

/**
 * Should `incoming` replace `current`?
 *
 *   - only a higher version is applied;
 *   - the same version is a duplicate and replays nothing;
 *   - a session that is finished never leaves its terminal state, whatever
 *     the version of the update that says otherwise (a delayed "charging"
 *     must not bring it back) — the caller should refetch instead.
 */
export function decide(current, incoming) {
  if (!isCanonicalSnapshot(incoming)) return DECISION.INVALID;
  if (!current) return DECISION.APPLY;
  if (incoming.sessionUid !== current.sessionUid) return DECISION.INVALID;
  if (incoming.version === current.version) return DECISION.DUPLICATE;
  if (incoming.version < current.version) return DECISION.STALE;
  if (isTerminalState(current.sessionState) && current.sessionState !== incoming.sessionState) {
    return DECISION.REGRESSION;
  }
  return DECISION.APPLY;
}

/**
 * Is the snapshot older than the freshness the API asks for?
 *
 * Terminal snapshots do not go stale: nothing more is expected. `receivedAt`
 * is when this device last heard about the session at all (any snapshot, the
 * same version included), so a quiet but connected session is not "stale".
 */
export function isStale(snapshot, receivedAt, now = Date.now()) {
  if (!snapshot || isTerminalState(snapshot.sessionState)) return false;
  const seconds = Number(snapshot.freshness?.staleAfterSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0 || !receivedAt) return false;
  return now - receivedAt > seconds * 1000;
}

/** Live polling is only worth doing while the session can still change. */
export function shouldKeepPolling(snapshot) {
  if (!snapshot) return true;
  return !(isTerminalState(snapshot.sessionState) && snapshot.chargingConfirmedStopped === true);
}
